import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Flip, ToastContainer } from "react-toastify";
import AuthContext from "store/auth-context";
import Header from "./components/layout/Header/Header";
import LeftSideBar from "./components/layout/LeftSideBar/LeftSideBar";
import useHttp from "hooks/use-http";
import { useStore } from "store/store";
import { getTitleSlug } from "helpers/services";
import { authorizedRoutes, unauthorizedRoutes } from "./routes/routes";
import Sitemap from "./routes/sitemap";
import PageWrapper from "components/layout/PageWrapper/PageWrapper";
import PageNotFound from "pages/PageNotFound/PageNotFound";
import useAPI from "./hooks/useAPI";
import "../src/components/Filter/rslider.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { I18nextProvider } from "react-i18next";
import axios from "axios";
import i18n from "i18next";
import Loading from "components/Loading/Loading";
import { Helmet } from "react-helmet";
import { initI18n } from "./config/i18n";

const client = new QueryClient();

// Minimum time between profile/user-data refreshes, so rapid navigation or
// focus events don't spam the API.
const USER_DATA_REFRESH_THROTTLE_MS = 30 * 1000;

function App() {
    const STAGING_API_URL = "b2b.staging.croonus.com";

    const location = useLocation();

    const api = useAPI();

    const apiRef = useRef(api);
    apiRef.current = api;

    // Authorization store
    const authCtx = useContext(AuthContext);

    const authCtxRef = useRef(authCtx);
    authCtxRef.current = authCtx;

    const lastUserDataFetchRef = useRef(0);

    // Throttled fetch of profile/user-data, shared by the navigation, focus,
    // and interval triggers below so bursts of any of them collapse into one request.
    const refreshUserData = useCallback(() => {
        const now = Date.now();
        if (now - lastUserDataFetchRef.current < USER_DATA_REFRESH_THROTTLE_MS) return;
        lastUserDataFetchRef.current = now;

        apiRef.current.get("/profile/user-data").then((response) => {
            if (response) {
                authCtxRef.current.refreshUser(response);
            }
        });
    }, []);

    // Use http instance for http handling
    const { sendRequest: request } = useHttp();

    const [openModal, setOpenModal] = useState("none");

    const dispatch = useStore()[1];

    const [i18nReady, setI18nReady] = useState(false);
    const [apiURL, setApiURL] = useState(false);
    const [errorApiURL, setErrorApiURL] = useState(false);

    useEffect(() => {
        if (authCtx.isLoggedIn && apiURL) {
            dispatch("CART", request).then();
        }
    }, [authCtx.isLoggedIn, request, apiURL]);

    // Refresh user/company data on login and on every page navigation (throttled).
    // Waits for apiURL so it doesn't fire against the build-time default backend
    // before the per-tenant API URL has been resolved (routing.php lookup below).
    useEffect(() => {
        if (!authCtx.isLoggedIn || !apiURL) return;
        refreshUserData();
    }, [authCtx.isLoggedIn, location.pathname, refreshUserData, apiURL]);

    // Fallback refresh: every 10 minutes, and whenever the tab regains focus (throttled).
    useEffect(() => {
        if (!authCtx.isLoggedIn || !apiURL) return;

        const interval = setInterval(refreshUserData, 10 * 60 * 1000);
        window.addEventListener("focus", refreshUserData);

        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", refreshUserData);
        };
    }, [authCtx.isLoggedIn, refreshUserData, apiURL]);

    useEffect(() => {
        if (!apiURL && !errorApiURL) {
            // Shared by both paths below: store the resolved API URL, fetch the default
            // language, init i18n, then mark the app ready to render.
            const applyApiUrl = async (finalApiUrl) => {
                localStorage.setItem("api", finalApiUrl);

                const defaultLanguage = await axios.get(`${finalApiUrl}configuration/b2b_basic_configuration/basic_view/lang/value`);
                if (defaultLanguage?.data) {
                    localStorage.setItem("selectedLanguage", defaultLanguage?.data);
                    localStorage.setItem("defaultLanguage", defaultLanguage?.data);
                }

                // inicijalizuj i18n
                await initI18n(finalApiUrl, defaultLanguage?.data);
                setI18nReady(true);
                setApiURL(finalApiUrl);
            };

            const onResolveError = async (error) => {
                localStorage.setItem("api", STAGING_API_URL);
                await initI18n(STAGING_API_URL);
                setErrorApiURL(true);
                setI18nReady(true);
                console.log(error);
            };

            const isLocal = window.location.hostname === "localhost";

            if (isLocal && process.env.REACT_APP_URL) {
                // Local dev: use the backend from .env directly, skip the routing lookup.
                applyApiUrl(process.env.REACT_APP_URL).catch(onResolveError);
            } else {
                // Server env (or local dev without REACT_APP_URL set): resolve the API URL per-domain.
                axios
                    .get(`https://ecommerce-config.croonus.com/b2b/routing.php?link=${isLocal ? STAGING_API_URL : window.location.host}`)
                    .then((response) => {
                        const react_app_url = response?.data?.env?.REACT_APP_URL;
                        const finalApiUrl = response?.data?.active && react_app_url ? react_app_url : STAGING_API_URL;

                        return applyApiUrl(finalApiUrl);
                    })
                    .catch(onResolveError);
            }
        }
    }, [apiURL, errorApiURL]);

    // Load configuration values
    useEffect(() => {
        // Title
        if (apiURL) getTitleSlug(apiURL).then((response) => (document.getElementById("TitleDOM").innerHTML = response.data));
    }, [apiURL]);

    if (!apiURL || !i18nReady) {
        return <Loading size={50} />;
    }

    return (
        <QueryClientProvider client={client}>
            <I18nextProvider i18n={i18n}>
                {authCtx.isLoggedIn && <Header openModal={openModal} setOpenModal={setOpenModal} />}
                <Helmet>
                    <link rel="icon" href={`${apiURL}configuration/images/presentation/basic/favicon`} />
                    <link rel="stylesheet" href={`${apiURL}configuration/style.css`} />
                </Helmet>
                <PageWrapper isLoggedIn={authCtx.isLoggedIn}>
                    {authCtx.isLoggedIn && <LeftSideBar openModal={openModal} setOpenModal={setOpenModal} />}
                    <Routes>
                        {authCtx.isLoggedIn && authorizedRoutes.map(([path, element]) => <Route path={path} element={element} key={path} />)}
                        {!authCtx.isLoggedIn && unauthorizedRoutes.map(([path, element]) => <Route path={path} element={element} key={path} />)}

                        {authCtx.isLoggedIn && unauthorizedRoutes.some(([path]) => path === location.pathname) && <Route path="*" element={<Navigate to={Sitemap.startPage} replace />} />}

                        <Route path={Sitemap.any} element={<PageNotFound />} />
                    </Routes>
                </PageWrapper>

                <ToastContainer position="top-center" theme="colored" transition={Flip} autoClose={800} newestOnTop={false} draggable={false} closeOnClick hideProgressBar pauseOnHover />
            </I18nextProvider>
        </QueryClientProvider>
    );
}

export default App;
